// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon, Cpu, Database, Shield,
  Save, RefreshCw,
  Monitor, Zap
} from 'lucide-react';
import { apiJSON, API_CONFIG } from '../config/api';
import notificationService, { handleApiError } from '../utils/notificationService';
import ApiDiagnostic from './ApiDiagnostic';
import AISelector from './AISelector';
import './Settings.css';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null); // FIX-010 (refs AUD-011): feedback chargement
  const [activeTab, setActiveTab] = useState('general');

  // Sélecteur de provider IA
  const [aiProviders, setAiProviders] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('ollama');
  const [selectedModel, setSelectedModel] = useState('');
  const [settings, setSettings] = useState({
    general: {
      app_name: 'Biblio-Tech RAG',
      language: 'fr',
      theme: 'auto',
      auto_save: true,
      notifications: true
    },
    ai: {
      default_model: 'llama3.1:8b',
      max_tokens: 2048,
      temperature: 0.7,
      chunk_size: 1000,
      chunk_overlap: 200,
      enable_streaming: true
    },
    performance: {
      cache_enabled: true,
      cache_ttl: 3600,
      max_concurrent_uploads: 3,
      enable_compression: true,
      memory_limit: '2GB'
    },
    security: {
      enable_auth: false,
      session_timeout: 3600,
      rate_limit_enabled: true,
      max_requests_per_minute: 100,
      enable_cors: true
    },
    storage: {
      max_file_size: '100MB',
      allowed_extensions: ['pdf', 'epub', 'txt', 'docx'],
      compression_level: 6,
      backup_retention_days: 30,
      enable_auto_cleanup: true
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      // Charger les paramètres depuis l'API
      try {
        const response = await apiJSON('settings/get');
        if (response.success) {
          setSettings(prev => ({ ...prev, ...response.settings }));
        }
      } catch (error) {
        console.warn('Impossible de charger les paramètres:', error.message);
        // Utiliser les paramètres par défaut
      }

      // Charger les providers IA disponibles
      try {
        const provResp = await apiJSON('ai/providers');
        if (provResp.success) {
          setAiProviders(provResp.providers || {});
          setSelectedProvider(provResp.active_provider || 'ollama');
          setSelectedModel(provResp.active_model || '');
        }
      } catch (e) {
        console.warn('Providers IA non disponibles:', e.message);
      }

    } catch (error) {
      setLoadError(error?.message || 'Impossible de charger les paramètres');
      handleApiError(error, 'Chargement paramètres');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const data = await apiJSON('settings/save', {
        method: 'POST',
        body: JSON.stringify(settings),
      });
      if (data.success) {
        notificationService.showToast('Paramètres sauvegardés avec succès', 'success');
      } else {
        notificationService.showToast('Erreur sauvegarde paramètres', 'error');
      }
    } catch (error) {
      handleApiError(error, 'Sauvegarde paramètres');
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    if (!window.confirm('Réinitialiser tous les paramètres aux valeurs par défaut ?')) {
      return;
    }

    try {
      setSaving(true);
      const data = await apiJSON('settings/reset', { method: 'POST' });
      if (data.success) {
        notificationService.showToast('Paramètres réinitialisés', 'success');
        await loadSettings();
      } else {
        notificationService.showToast('Erreur réinitialisation', 'error');
      }
    } catch (error) {
      handleApiError(error, 'Réinitialisation paramètres');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  // Handlers pour le sélecteur de provider IA
  const handleProviderChange = async (provider) => {
    setSelectedProvider(provider);
    try {
      const resp = await apiJSON(
        `ai/provider?provider=${encodeURIComponent(provider)}`,
        { method: 'POST' }
      );
      if (resp.success) {
        setSelectedModel(resp.active_model || '');
        notificationService.showToast(`Provider IA : ${provider}`, 'success');
      }
    } catch (e) {
      console.warn('Erreur changement provider IA:', e.message);
    }
  };

  const handleModelChange = async (model) => {
    setSelectedModel(model);
    try {
      await apiJSON(
        `ai/provider?provider=${encodeURIComponent(selectedProvider)}&model=${encodeURIComponent(model)}`,
        { method: 'POST' }
      );
    } catch (e) {
      console.warn('Erreur changement modèle IA:', e.message);
    }
  };

  // Identifiant stable par réglage : il relie le <label> (htmlFor) et la
  // description (aria-describedby) au champ. Sans lui, un lecteur d'écran
  // annonçait « case à cocher » ou « zone d'édition » sans dire laquelle.
  const idReglage = (category, key) => `reglage-${category}-${key}`;

  const renderInput = (category, key, type = 'text', options = {}, aDescription = false) => {
    const value = settings[category][key];
    const id = idReglage(category, key);
    const describedBy = aDescription ? `${id}-description` : undefined;
    
    switch (type) {
      case 'boolean':
        return (
          <label className="setting-toggle">
            <input
              id={id}
              aria-describedby={describedBy}
              type="checkbox"
              checked={value}
              onChange={(e) => updateSetting(category, key, e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        );
        
      case 'select':
        return (
          <select
            id={id}
            aria-describedby={describedBy}
            value={value}
            onChange={(e) => updateSetting(category, key, e.target.value)}
            className="setting-input"
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        
      case 'number':
        return (
          <input
            id={id}
            aria-describedby={describedBy}
            type="number"
            value={value}
            onChange={(e) => updateSetting(category, key, Number(e.target.value))}
            className="setting-input"
            min={options.min}
            max={options.max}
            step={options.step}
          />
        );
        
      default:
        return (
          <input
            id={id}
            aria-describedby={describedBy}
            type="text"
            value={value}
            onChange={(e) => updateSetting(category, key, e.target.value)}
            className="setting-input"
            placeholder={options.placeholder}
          />
        );
    }
  };

  const renderSetting = (category, key, label, type = 'text', options = {}, description = '') => (
    <div className="setting-item">
      <div className="setting-label">
        <label htmlFor={idReglage(category, key)}>{label}</label>
        {description && (
          <p className="setting-description" id={`${idReglage(category, key)}-description`}>
            {description}
          </p>
        )}
      </div>
      <div className="setting-control">
        {renderInput(category, key, type, options, Boolean(description))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="settings mx-auto flex min-h-[40vh] max-w-5xl flex-col items-center justify-center gap-4 px-4 py-8">
        <div className="loading flex flex-col items-center gap-3 text-neutral-600">
          <RefreshCw className="spinner" />
          <p className="text-sm">Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings mx-auto max-w-5xl space-y-6 px-4 py-6 md:px-6">
      {loadError && (
        <div
          className="settings-load-error rounded-lg border border-danger/20 bg-red-50 p-3 text-sm text-danger"
          role="alert"
        >
          {loadError} — Les valeurs par défaut sont affichées.
        </div>
      )}
      <div className="settings-header">
        <h1><SettingsIcon /> Paramètres</h1>
        <p>Configuration globale de l'application</p>
      </div>

      {/* Onglets de navigation */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <SettingsIcon /> Général
        </button>
        <button 
          className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          <Cpu /> IA
        </button>
        <button 
          className={`tab ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          <Zap /> Performance
        </button>
        <button 
          className={`tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <Shield /> Sécurité
        </button>
        <button 
          className={`tab ${activeTab === 'storage' ? 'active' : ''}`}
          onClick={() => setActiveTab('storage')}
        >
          <Database /> Stockage
        </button>
        <button 
          className={`tab ${activeTab === 'diagnostic' ? 'active' : ''}`}
          onClick={() => setActiveTab('diagnostic')}
        >
          <Monitor /> Diagnostic
        </button>
      </div>

      {/* Contenu des onglets */}
      <div className="tab-content">
        {/* Onglet Général */}
        {activeTab === 'general' && (
          <div className="settings-section">
            <div className="section-header">
              <h2><SettingsIcon /> Paramètres Généraux</h2>
            </div>
            
            <div className="settings-grid">
              {renderSetting(
                'general', 'app_name', 'Nom de l\'application',
                'text', { placeholder: 'Biblio-Tech RAG' },
                'Nom affiché dans l\'interface utilisateur'
              )}
              
              {/* Sélecteurs « Langue » et « Thème » retirés : leur valeur était
                  bien enregistrée côté serveur, mais rien ne la relisait pour
                  l'appliquer. Le projet n'embarque aucune bibliothèque i18n, et
                  l'attribut data-theme est posé depuis les préférences locales
                  (AppContext), qu'aucun écran ne met à jour. Les rétablir
                  suppose de brancher l'application effective du réglage. */}

              {renderSetting(
                'general', 'auto_save', 'Sauvegarde automatique',
                'boolean', {},
                'Sauvegarder automatiquement les modifications'
              )}
              
              {renderSetting(
                'general', 'notifications', 'Notifications',
                'boolean', {},
                'Afficher les notifications système'
              )}
            </div>
          </div>
        )}

        {/* Onglet IA */}
        {activeTab === 'ai' && (
          <div className="settings-section">
            <div className="section-header">
              <h2><Cpu /> Configuration IA</h2>
            </div>

            {/* Sélecteur de provider IA */}
            <AISelector
              providers={aiProviders}
              selectedProvider={selectedProvider}
              selectedModel={selectedModel}
              onProviderChange={handleProviderChange}
              onModelChange={handleModelChange}
            />

            <div className="settings-grid" style={{ marginTop: '2rem' }}>
              {/* Liste de modèles en dur retirée : elle ne correspondait pas aux
                  modèles réellement installés. Le sélecteur AISelector ci-dessus
                  est alimenté par GET /api/ai/providers, seule source fiable. */}

              {renderSetting(
                'ai', 'max_tokens', 'Tokens maximum',
                'number', { min: 100, max: 8192, step: 100 },
                'Nombre maximum de tokens par réponse'
              )}
              
              {renderSetting(
                'ai', 'temperature', 'Température',
                'number', { min: 0, max: 2, step: 0.1 },
                'Contrôle la créativité des réponses (0 = déterministe, 2 = très créatif)'
              )}
              
              {renderSetting(
                'ai', 'chunk_size', 'Taille des chunks',
                'number', { min: 100, max: 4000, step: 100 },
                'Taille des segments de texte pour l\'indexation'
              )}
              
              {renderSetting(
                'ai', 'chunk_overlap', 'Chevauchement des chunks',
                'number', { min: 0, max: 1000, step: 50 },
                'Chevauchement entre les segments de texte'
              )}
              
              {renderSetting(
                'ai', 'enable_streaming', 'Réponses en streaming',
                'boolean', {},
                'Afficher les réponses IA en temps réel'
              )}
            </div>
          </div>
        )}

        {/* Onglet Performance */}
        {activeTab === 'performance' && (
          <div className="settings-section">
            <div className="section-header">
              <h2><Zap /> Optimisation Performance</h2>
            </div>
            
            <div className="settings-grid">
              {renderSetting(
                'performance', 'cache_enabled', 'Cache activé',
                'boolean', {},
                'Utiliser le cache pour améliorer les performances'
              )}
              
              {renderSetting(
                'performance', 'cache_ttl', 'Durée de vie du cache (secondes)',
                'number', { min: 60, max: 86400, step: 60 },
                'Durée de conservation des données en cache'
              )}
              
              {renderSetting(
                'performance', 'max_concurrent_uploads', 'Uploads simultanés max',
                'number', { min: 1, max: 10, step: 1 },
                'Nombre maximum d\'uploads simultanés'
              )}
              
              {renderSetting(
                'performance', 'enable_compression', 'Compression activée',
                'boolean', {},
                'Compresser les données pour économiser l\'espace'
              )}
              
              {renderSetting(
                'performance', 'memory_limit', 'Limite mémoire',
                'select', [
                  { value: '1GB', label: '1 GB' },
                  { value: '2GB', label: '2 GB' },
                  { value: '4GB', label: '4 GB' },
                  { value: '8GB', label: '8 GB' }
                ],
                'Limite de mémoire pour l\'application'
              )}
            </div>
          </div>
        )}

        {/* Onglet Sécurité */}
        {activeTab === 'security' && (
          <div className="settings-section">
            <div className="section-header">
              <h2><Shield /> Sécurité</h2>
            </div>
            
            <div className="settings-grid">
              {renderSetting(
                'security', 'enable_auth', 'Authentification activée',
                'boolean', {},
                'Activer l\'authentification utilisateur'
              )}
              
              {renderSetting(
                'security', 'session_timeout', 'Timeout session (secondes)',
                'number', { min: 300, max: 86400, step: 300 },
                'Durée d\'inactivité avant déconnexion automatique'
              )}
              
              {renderSetting(
                'security', 'rate_limit_enabled', 'Limitation de débit',
                'boolean', {},
                'Limiter le nombre de requêtes par minute'
              )}
              
              {renderSetting(
                'security', 'max_requests_per_minute', 'Requêtes max/min',
                'number', { min: 10, max: 1000, step: 10 },
                'Nombre maximum de requêtes par minute'
              )}
              
              {renderSetting(
                'security', 'enable_cors', 'CORS activé',
                'boolean', {},
                'Autoriser les requêtes cross-origin'
              )}
            </div>
            {/* Lot 0.23 (audit 2026-10-22) : l'ancien champ « Clé API » était
                pré-rempli avec le credential de SESSION, puis l'écrasait sans
                validation (jeton Supabase remplacé par une saisie quelconque). Aucune autre lecture n'existait : le seul lecteur de
                ce credential est `getAuthHeaders()`. La connexion par clé API
                existe déjà, validée côté serveur, sur l'écran de connexion. */}
            <div className="settings-grid" style={{ marginTop: '1rem' }}>
              <div className="setting-item">
                <div className="setting-label">
                  {/* Aucun champ ici : un <label> sans contrôle associé est
                      signalé par les outils d'accessibilité. */}
                  <h3 className="setting-label-titre">Clé API</h3>
                  <p className="setting-description">
                    Pour vous authentifier par clé API (AUTH_API_KEY), déconnectez-vous puis
                    saisissez-la sur l’écran de connexion : elle y est vérifiée par le serveur
                    avant d’être utilisée.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Stockage */}
        {activeTab === 'storage' && (
          <div className="settings-section">
            <div className="section-header">
              <h2><Database /> Gestion Stockage</h2>
            </div>
            
            <div className="settings-grid">
              {renderSetting(
                'storage', 'max_file_size', 'Taille fichier max',
                'select', [
                  { value: '10MB', label: '10 MB' },
                  { value: '50MB', label: '50 MB' },
                  { value: '100MB', label: '100 MB' },
                  { value: '500MB', label: '500 MB' }
                ],
                'Taille maximale des fichiers uploadés'
              )}
              
              {renderSetting(
                'storage', 'compression_level', 'Niveau compression',
                'number', { min: 0, max: 9, step: 1 },
                'Niveau de compression des fichiers (0 = aucun, 9 = maximum)'
              )}
              
              {renderSetting(
                'storage', 'backup_retention_days', 'Rétention backups (jours)',
                'number', { min: 1, max: 365, step: 1 },
                'Durée de conservation des sauvegardes automatiques'
              )}
              
              {renderSetting(
                'storage', 'enable_auto_cleanup', 'Nettoyage automatique',
                'boolean', {},
                'Nettoyer automatiquement les fichiers temporaires'
              )}
            </div>
          </div>
        )}

        {/* Onglet Diagnostic */}
        {activeTab === 'diagnostic' && (
          <div className="settings-section">
            <div className="section-header">
              <h2><Monitor /> Diagnostic API</h2>
            </div>
            
            <div className="diagnostic-section">
              <ApiDiagnostic />
              
              <div className="api-info">
                <h3>Informations API</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <strong>BASE_API:</strong> 
                    <code>{API_CONFIG.API_BASE_URL}</code>
                  </div>
                  {/* Ligne « Version » retirée : « 1.0.0 » était un littéral,
                      sans rapport avec la version réellement déployée. */}
                  <div className="info-item">
                    <strong>Environnement:</strong> 
                    <span>{process.env.NODE_ENV}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="settings-actions">
        <button 
          onClick={saveSettings}
          disabled={saving}
          className="btn-primary"
        >
          <Save />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
        
        <button 
          onClick={resetSettings}
          disabled={saving}
          className="btn-warning"
        >
          <RefreshCw />
          Réinitialiser
        </button>
      </div>
    </div>
  );
};

export default Settings;
